import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { PJResultsType } from "../calculadora-pj";

// Constantes para formatação
const formatarMoedaPtBR = (value: number): string => {
  return value.toFixed(2).replace('.', ',');
};

export const PJResponse = ({ results }: { results: PJResultsType }) => {
  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado do Cálculo</MainCardTitle>
      
      {/* Cards de Resumo Principal - 2 Colunas */}
      <div className="grid sm:grid-cols-2 gap-4 mb-2 grid-cols-1">
        <div className="p-3 bg-primary rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center text-white font-semibold m-0">
            Valor Líquido Mensal
          </h2>
          <p className="sm:text-xl text-center font-bold text-md text-white m-0">
            <span className="text-sm mr-1 text-white">R$</span>
            {normalSanatizerBRL(results.valorLiquido)}
          </p>
          <p className="text-xs text-center text-white opacity-90 mt-1">
            {formatarMoedaPtBR(results.percentualLiquido)}% da renda bruta
          </p>
        </div>
        
        <div className="p-3 bg-white rounded border flex flex-col justify-center">
          <h2 className="sm:text-base text-sm text-center font-semibold m-0">
            Renda Bruta Mensal
          </h2>
          <p className="sm:text-lg text-center font-black text-md text-red-800 m-0">
            <span className="text-sm mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(results.remuneracaoBruta)}
          </p>
          <p className="text-xs text-center text-gray-600 mt-1">
            Valor total informado
          </p>
        </div>
      </div>

      {/* Informação do Regime */}
      <div className="mb-6 p-4 bg-gray-50 rounded border text-center">
        <p className="text-sm text-gray-600 mb-1">
          Regime: {results.regime}
          {results.anexo && ` - Anexo ${results.anexo}`}
          {results.tipoAtividade && ` - ${results.tipoAtividade}`}
          {results.aliquota && ` (${formatarMoedaPtBR(results.aliquota)}%)`}
        </p>
        <p className="text-sm text-gray-600">
          Cálculo com valores 2025 atualizados
        </p>
      </div>

      {/* Tributos e Contribuições */}
      <h2 className="font-bold text-primary text-lg mt-8 text-center">
        Tributos e Contribuições:
      </h2>
      
      {/* Tributo principal */}
      <div className="grid sm:grid-cols-2 gap-4 mb-6 grid-cols-1">
        {results.tipo === 'MEI' && (
          <div className="p-3 bg-red-50 rounded border border-red-200">
            <h3 className="text-sm font-semibold text-center mb-1">DAS MEI</h3>
            <p className="text-lg font-bold text-center text-red-600">
              R$ {normalSanatizerBRL(results.dasMEI!)}
            </p>
            <p className="text-xs text-center text-red-700">Taxa fixa mensal</p>
          </div>
        )}

        {results.tipo === 'Simples Nacional' && (
          <div className="p-3 bg-red-50 rounded border border-red-200">
            <h3 className="text-sm font-semibold text-center mb-1">Simples Nacional</h3>
            <p className="text-lg font-bold text-center text-red-600">
              R$ {normalSanatizerBRL(results.impostoSimples!)}
            </p>
            <p className="text-xs text-center text-red-700">
              Anexo {results.anexo} - {formatarMoedaPtBR(results.aliquota!)}%
            </p>
          </div>
        )}

        {results.tipo === 'Lucro Presumido' && (
          <div className="p-3 bg-red-50 rounded border border-red-200">
            <h3 className="text-sm font-semibold text-center mb-1">Lucro Presumido</h3>
            <p className="text-lg font-bold text-center text-red-600">
              R$ {normalSanatizerBRL(results.impostoPresumido!)}
            </p>
            <p className="text-xs text-center text-red-700">
              {formatarMoedaPtBR(results.aliquota!)}% efetiva
            </p>
          </div>
        )}

        <div className="p-3 bg-red-50 rounded border border-red-200">
          <h3 className="text-sm font-semibold text-center mb-1">INSS Pró-labore</h3>
          <p className="text-lg font-bold text-center text-red-600">
            R$ {normalSanatizerBRL(results.inssProLabore)}
          </p>
          <p className="text-xs text-center text-red-700">
            11% sobre R$ {normalSanatizerBRL(results.proLabore)}
          </p>
        </div>
      </div>

      {/* IRRF - só mostra se houver */}
      {results.irrf.valor > 0 && (
        <div className="grid sm:grid-cols-1 gap-4 mb-6 grid-cols-1">
          <div className="p-3 bg-red-50 rounded border border-red-200">
            <h3 className="text-sm font-semibold text-center mb-1">IRRF Pró-labore</h3>
            <p className="text-lg font-bold text-center text-red-600">
              R$ {normalSanatizerBRL(results.irrf.valor)}
            </p>
            <p className="text-xs text-center text-red-700">
              {formatarMoedaPtBR(results.irrf.aliquota)}% sobre base de R$ {normalSanatizerBRL(results.irrf.baseCalculo)}
              {results.irrf.deducaoDependentes > 0 && (
                <span> | Dedução: R$ {normalSanatizerBRL(results.irrf.deducaoDependentes)}</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Detalhamento do Lucro Presumido */}
      {results.tipo === 'Lucro Presumido' && results.detalhesImpostos && (
        <>
          <h2 className="font-bold text-primary text-lg my-6 text-center">
            Detalhamento Lucro Presumido:
          </h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-6 grid-cols-1">
            <div className="p-3 bg-white rounded border">
              <h4 className="text-sm font-semibold text-center mb-1">IRPJ + CSLL</h4>
              <p className="text-md font-bold text-center text-gray-800">
                R$ {normalSanatizerBRL(results.detalhesImpostos.irpj + results.detalhesImpostos.csll)}
              </p>
              <p className="text-xs text-center text-gray-600">
                Sobre base presumida ({results.tipoAtividade === 'Prestação de Serviços' ? '32%' : '8%'})
              </p>
            </div>
            
            <div className="p-3 bg-white rounded border">
              <h4 className="text-sm font-semibold text-center mb-1">PIS + COFINS + ISS</h4>
              <p className="text-md font-bold text-center text-gray-800">
                R$ {normalSanatizerBRL(results.detalhesImpostos.pis + results.detalhesImpostos.cofins + results.detalhesImpostos.iss)}
              </p>
              <p className="text-xs text-center text-gray-600">
                Sobre receita bruta (6,65% total)
              </p>
            </div>
          </div>
        </>
      )}

      {/* Total de Descontos */}
      <div className="p-4 bg-gray-50 rounded border-2 border-gray-300 mb-6">
        <div className="flex justify-between items-center">
          <span className="font-bold text-gray-700 text-lg">Total de Descontos:</span>
          <span className="font-bold text-xl text-red-600">
            R$ {normalSanatizerBRL(results.totalDescontos)}
          </span>
        </div>
      </div>

      {/* Observações */}
      {results.observacoes.length > 0 && (
        <>
          <h2 className="font-bold text-primary text-lg my-6 text-center">
            Observações:
          </h2>
          <div className="p-3 bg-blue-50 rounded border border-blue-200 mb-6">
            <div className="text-sm space-y-1 text-blue-800">
              {results.observacoes.map((obs, index) => (
                <p key={index}>• {obs}</p>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Detalhes dos Cálculos */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Detalhes dos Cálculos:
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
        <div className="p-3 bg-white rounded border">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">
            {results.regime}:
          </h4>
          {results.tipo === 'MEI' ? (
            <>
              <p className="text-xs text-gray-600 mb-1">Taxa fixa mensal</p>
              <p className="text-xs text-gray-600 mb-1">Limite anual: R$ 81.000</p>
              <p className="text-xs text-gray-600">Valor: R$ {normalSanatizerBRL(results.dasMEI!)}</p>
            </>
          ) : results.tipo === 'Simples Nacional' ? (
            <>
              <p className="text-xs text-gray-600 mb-1">Anexo {results.anexo}</p>
              <p className="text-xs text-gray-600 mb-1">Alíquota: {formatarMoedaPtBR(results.aliquota!)}%</p>
              <p className="text-xs text-gray-600">Faturamento anual estimado: R$ {normalSanatizerBRL(results.remuneracaoBruta * 12)}</p>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-600 mb-1">{results.tipoAtividade}</p>
              <p className="text-xs text-gray-600 mb-1">Alíquota efetiva: {formatarMoedaPtBR(results.aliquota!)}%</p>
              <p className="text-xs text-gray-600">Sem limite de faturamento</p>
            </>
          )}
        </div>
        
        <div className="p-3 bg-white rounded border">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">Pró-labore:</h4>
          <p className="text-xs text-gray-600 mb-1">Valor: R$ {normalSanatizerBRL(results.proLabore)}</p>
          <p className="text-xs text-gray-600 mb-1">INSS: 11% = R$ {normalSanatizerBRL(results.inssProLabore)}</p>
          {results.irrf.valor > 0 ? (
            <p className="text-xs text-gray-600">IRRF: {formatarMoedaPtBR(results.irrf.aliquota)}% = R$ {normalSanatizerBRL(results.irrf.valor)}</p>
          ) : (
            <p className="text-xs text-gray-600">IRRF: Isento</p>
          )}
        </div>
      </div>

      {/* Comparativo dos Regimes */}
      <h2 className="font-bold text-primary text-lg my-6 text-center">
        Comparativo dos Regimes:
      </h2>
      <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
        <div className={`p-3 rounded border ${results.tipo === 'MEI' ? 'bg-red-50 border-red-300' : 'bg-white'}`}>
          <h3 className="font-semibold text-gray-800 text-sm mb-2">MEI</h3>
          <p className="text-xs text-gray-600 mb-1">Limite: R$ 81.000/ano</p>
          <p className="text-xs text-gray-600 mb-1">Taxa fixa: ~R$ 77-82/mês</p>
          <p className="text-xs text-gray-600">Pró-labore opcional</p>
        </div>
        
        <div className={`p-3 rounded border ${results.tipo === 'Simples Nacional' ? 'bg-red-50 border-red-300' : 'bg-white'}`}>
          <h3 className="font-semibold text-gray-800 text-sm mb-2">Simples Nacional</h3>
          <p className="text-xs text-gray-600 mb-1">Limite: R$ 4.800.000/ano</p>
          <p className="text-xs text-gray-600 mb-1">Taxa: 4% a 33%</p>
          <p className="text-xs text-gray-600">Pró-labore obrigatório</p>
        </div>
        
        <div className={`p-3 rounded border ${results.tipo === 'Lucro Presumido' ? 'bg-red-50 border-red-300' : 'bg-white'} md:col-span-2`}>
          <h3 className="font-semibold text-gray-800 text-sm mb-2">Lucro Presumido</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-gray-600 mb-1">Sem limite de faturamento</p>
              <p className="text-xs text-gray-600">Pró-labore obrigatório</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Taxa: ~11% a 16%</p>
              <p className="text-xs text-gray-600">Ideal para grandes faturamentos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Informações Importantes */}
      <div className="mt-6 p-4 bg-yellow-50 rounded border border-yellow-200">
        <h4 className="font-bold text-yellow-800 text-sm mb-2">
          Como Funciona a Tributação PJ - 2025
        </h4>
        <div className="text-sm text-yellow-800 space-y-1">
          {results.tipo === 'MEI' ? (
            <>
              <p>• O MEI paga uma taxa fixa mensal (DAS) que varia conforme a atividade</p>
              <p>• Limite: R$ 81.000,00 anuais (R$ 6.750,00 mensais)</p>
              <p>• Valores DAS 2025: Comércio R$ 76,90 | Serviços R$ 80,90 | Misto R$ 81,90</p>
              <p>• Pró-labore opcional; se informado, incidem INSS (11%) e IRRF</p>
            </>
          ) : results.tipo === 'Simples Nacional' ? (
            <>
              <p>• Alíquotas variam de 4% a 33% conforme faturamento e anexo</p>
              <p>• Anexo {results.anexo}: {formatarMoedaPtBR(results.aliquota!)}% sobre o faturamento</p>
              <p>• Pró-labore obrigatório: INSS (11%) e IRRF conforme tabela</p>
              <p>• Distribuição de lucros sem tributação adicional na pessoa física</p>
            </>
          ) : (
            <>
              <p>• IRPJ (15%) e CSLL (9%) sobre base presumida conforme atividade</p>
              <p>• PIS (0,65%) e COFINS (3%) sobre receita bruta</p>
              <p>• ISS varia por município (2% a 5%), média de 3%</p>
              <p>• Pró-labore obrigatório: INSS (11%) e IRRF conforme tabela</p>
              <p>• Sem limite de faturamento, ideal para empresas maiores</p>
            </>
          )}
        </div>
        <div className="mt-2 p-2 bg-yellow-100 rounded border border-yellow-300">
          <p className="text-xs text-yellow-800">
            <strong>Atualizações 2025:</strong> Salário mínimo R$ 1.518,00 | DAS MEI reajustado | 
            Tabela IRRF Lei nº 14.848/2024 | Cálculos Lucro Presumido implementados
          </p>
        </div>
      </div>
    </MainCard>
  );
};